import { useState, useEffect, useMemo } from 'react'
import { toLocalDateString } from '../../utils/dateUtils'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { PDFViewer } from '@react-pdf/renderer'
import { StatementConfigSidebar } from './StatementConfigSidebar'
import BranchStatementTemplate from '@/components/invoice/BranchStatementTemplate'
import InvoicePDFGenerator from '@/components/invoice/InvoicePDFGenerator'
import { reconcileCustomer } from '@/utils/reconciliation'
import { generateAndUploadStatement } from '@/utils/statementPDFUploader'
import { supabase } from '@/supabase-client'

export function StatementPreviewModal({
  open,
  onOpenChange,
  customerId,
  customerName,
  branches = [],
  farmInfo = {}
}) {
  const [statementType, setStatementType] = useState('since_zero')
  const [transactionCount, setTransactionCount] = useState(10)
  const [dateRange, setDateRange] = useState({ start: '', end: '' })
  const [isSending, setIsSending] = useState(false)
  const [sendStatus, setSendStatus] = useState(null)

  // Data for preview
  const [invoiceData, setInvoiceData] = useState([])
  const [paymentData, setPaymentData] = useState([])
  const [latestInvoice, setLatestInvoice] = useState(null)
  const [loading, setLoading] = useState(true)

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSendStatus(null)
    }
  }, [open])

  // Fetch data when modal opens
  useEffect(() => {
    if (!open || !customerId) return

    const fetchData = async () => {
      setLoading(true)
      try {
        // Fetch invoice balances
        const { data: invoices } = await supabase
          .from('invoice_balances')
          .select('*')
          .eq('customer_id', customerId)
          .order('date', { ascending: true })

        // Fetch payments
        const { data: payments } = await supabase
          .from('payments')
          .select('id, date, amount, method')
          .eq('customer_id', customerId)
          .order('date', { ascending: true })

        // Fetch latest authorised invoice (for attachment target)
        const { data: latestInv } = await supabase
          .from('invoices')
          .select('id, invoice_no, xero_invoice_id')
          .eq('customer_id', customerId)
          .in('xero_invoice_status', ['authorised', 'submitted'])
          .order('date', { ascending: false })
          .limit(1)
          .single()

        setInvoiceData(invoices || [])
        setPaymentData(payments || [])
        setLatestInvoice(latestInv || null)
      } catch (err) {
        console.error('Error fetching statement data:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [open, customerId])

  // Generate statement entries for preview
  const statementEntries = useMemo(() => {
    const invoiceEntries = invoiceData.map(inv => ({
      date: inv.date,
      invoice_no: inv.invoice_no,
      total: inv.invoice_total,
      paid: inv.calculated_status === 'paid'
    }))

    const paymentEntries = paymentData.map(p => ({
      date: p.date,
      amount: p.amount,
      method: p.method
    }))

    return InvoicePDFGenerator.generateMiniStatement(
      invoiceEntries,
      paymentEntries,
      {
        type: statementType,
        transactionCount,
        startDate: dateRange.start || null,
        endDate: dateRange.end || null
      }
    )
  }, [invoiceData, paymentData, statementType, transactionCount, dateRange])

  // Build statement data for the template
  const statementData = useMemo(() => {
    const totalInvoiced = statementEntries
      .filter(e => e.type === 'invoice')
      .reduce((sum, e) => sum + e.amount, 0)
    const totalPaid = statementEntries
      .filter(e => e.type === 'payment')
      .reduce((sum, e) => sum + Math.abs(e.amount), 0)

    return {
      branch: {
        branchName: branches.length === 1 ? branches[0]?.branchName : customerName,
        invoices: invoiceData
          .filter(i =>
            statementEntries.some(e => e.description?.includes(i.invoice_no))
          )
          .map(i => ({
            id: i.invoice_id,
            date: i.date,
            invoiceNo: i.invoice_no,
            total: i.invoice_total,
            paid: i.calculated_status === 'paid'
          })),
        payments: paymentData.map(p => ({
          id: p.id,
          date: p.date,
          method: p.method,
          amount: p.amount
        }))
      },
      customerInfo: { name: customerName },
      startDate: dateRange.start || null,
      endDate: dateRange.end || toLocalDateString(new Date()),
      generatedAt: new Date().toISOString(),
      totals: {
        totalInvoiced,
        totalPaid,
        totalOutstanding: totalInvoiced - totalPaid,
        currentBalance: totalInvoiced - totalPaid
      }
    }
  }, [statementEntries, invoiceData, paymentData, branches, customerName, dateRange])

  const handleSend = async () => {
    if (!latestInvoice?.xero_invoice_id) return

    setIsSending(true)
    setSendStatus(null)

    try {
      // Step 1: Reconcile with Xero
      await reconcileCustomer(customerId)

      // Step 2: Generate and upload statement PDF
      const storagePath = await generateAndUploadStatement(
        customerId,
        statementType,
        {
          farmInfo,
          customerInfo: { name: customerName },
          transactionCount,
          startDate: dateRange.start,
          endDate: dateRange.end
        }
      )

      // Step 3: Call attach-statement Edge Function
      const { data, error } = await supabase.functions.invoke('xero-invoices', {
        body: {
          action: 'attach-statement',
          xero_invoice_id: latestInvoice.xero_invoice_id,
          statement_storage_path: storagePath
        }
      })

      if (error) throw error
      if (!data?.success) throw new Error(data?.error || 'Failed to attach statement')

      setSendStatus('success')
    } catch (err) {
      console.error('Error sending statement:', err)
      setSendStatus('error')
    } finally {
      setIsSending(false)
    }
  }

  const canSend = !!latestInvoice?.xero_invoice_id && !loading

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl h-[80vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle>Customer Statement — {customerName}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-1 min-h-0">
          <StatementConfigSidebar
            statementType={statementType}
            onStatementTypeChange={setStatementType}
            transactionCount={transactionCount}
            onTransactionCountChange={setTransactionCount}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            latestInvoiceNo={latestInvoice?.invoice_no}
            onSend={handleSend}
            isSending={isSending}
            canSend={canSend}
          />

          <div className="flex-1 min-h-0">
            {loading ? (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                Loading statement data...
              </div>
            ) : (
              <PDFViewer width="100%" height="100%" showToolbar={false}>
                <BranchStatementTemplate
                  statementData={statementData}
                  farmInfo={farmInfo}
                />
              </PDFViewer>
            )}
          </div>
        </div>

        {sendStatus === 'success' && (
          <div className="px-6 py-3 bg-green-50 border-t text-sm text-green-700">
            Statement successfully attached to invoice {latestInvoice?.invoice_no}
          </div>
        )}
        {sendStatus === 'error' && (
          <div className="px-6 py-3 bg-red-50 border-t text-sm text-red-700">
            Failed to send statement. Please try again.
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
